import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Loader2, AlertTriangle, Brain, Zap, Target, Shield } from 'lucide-react';
import * as API from '../../lib/api';

const LAYER_INFO = {
  gate: { 
    name: 'Pattern Gate', 
    icon: Shield, 
    description: 'Checking for viable pattern...',
    color: 'text-amber-400'
  },
  fast: { 
    name: 'Quick Read', 
    icon: Zap, 
    description: 'Initial structure analysis',
    color: 'text-cyan-400'
  },
  mid: { 
    name: 'Deep Analysis', 
    icon: Brain, 
    description: 'Detailed rule verification',
    color: 'text-purple-400'
  },
  deep: { 
    name: 'Expert Review', 
    icon: Target, 
    description: 'Comprehensive validation',
    color: 'text-green-400'
  },
};

const LayerStatus = ({ layer, result }) => {
  const info = LAYER_INFO[layer];
  const Icon = info.icon;
  
  const statusIcon = {
    pending: <div className="w-4 h-4 rounded-full border-2 border-zinc-600" />,
    processing: <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />,
    complete: <CheckCircle className="w-4 h-4 text-green-400" />,
    failed: <XCircle className="w-4 h-4 text-red-400" />,
  };

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
      result.status === 'processing' ? 'bg-yellow-500/10 border border-yellow-500/30' :
      result.status === 'complete' ? 'bg-green-500/10 border border-green-500/30' :
      result.status === 'failed' ? 'bg-red-500/10 border border-red-500/30' :
      'bg-zinc-800/50 border border-zinc-700/50'
    }`}>
      <Icon className={`w-5 h-5 ${info.color}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-white text-sm">{info.name}</span>
          {result.model_used && (
            <span className="text-[10px] text-zinc-500 font-mono truncate">
              {result.model_used.split('/').pop()}
            </span>
          )}
        </div>
        <div className="text-xs text-zinc-400">
          {result.status === 'processing' ? info.description :
           result.status === 'complete' ? `Confidence: ${(result.confidence * 100).toFixed(0)}%` :
           result.status === 'failed' ? result.error || 'Failed' :
           'Waiting...'}
        </div>
      </div>
      {statusIcon[result.status]}
      {result.duration_ms > 0 && (
        <span className="text-[10px] text-zinc-500 font-mono">
          {(result.duration_ms / 1000).toFixed(1)}s
        </span>
      )}
    </div>
  );
};

export default function ConsensusOverlay({ 
  analysisId, 
  onComplete, 
  onGateFailed,
  onLayerComplete 
}) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  
  const pollStatus = useCallback(async () => {
    try {
      const data = await API.getConsensusStatus(analysisId);
      setStatus(data);
      
      // Notify parent of layer completions
      if (data.consensus_progress?.layers) {
        Object.entries(data.consensus_progress.layers).forEach(([layer, result]) => {
          if (result.status === 'complete' && result.pivots) {
            onLayerComplete?.(layer, result.pivots, result.confidence);
          }
        });
      }
      
      // Check for completion or gate failure
      if (data.is_final) {
        if (data.gate_passed === false) {
          onGateFailed?.(data);
        } else {
          onComplete?.(data.current_consensus);
        }
        return true; // Stop polling
      }
      
      return false;
    } catch (err) {
      console.error('Consensus poll error:', err);
      setError(err.message);
      return false;
    }
  }, [analysisId, onComplete, onGateFailed, onLayerComplete]);
  
  useEffect(() => {
    let active = true;
    let timeoutId;
    
    const poll = async () => {
      if (!active) return;
      const done = await pollStatus();
      if (!done && active) {
        timeoutId = setTimeout(poll, 1500);
      }
    };
    
    poll();
    
    return () => {
      active = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [pollStatus]);
  
  if (error) {
    return (
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-red-900/50 border border-red-500/50 rounded-lg p-6 max-w-md text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-white mb-2">Analysis Error</h3>
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      </div>
    );
  }
  
  // Gate failed state
  if (status?.gate_passed === false && status?.is_final) {
    return (
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-zinc-900 border border-amber-500/50 rounded-xl p-8 max-w-lg text-center shadow-2xl">
          <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-3">No Credible Pattern Detected</h3>
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="h-2 w-32 bg-zinc-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-amber-500 rounded-full" 
                style={{ width: `${(status.gate_confidence || 0) * 100}%` }}
              />
            </div>
            <span className="text-amber-400 font-mono text-sm">
              {((status.gate_confidence || 0) * 100).toFixed(0)}%
            </span>
          </div>
          <p className="text-zinc-400 mb-6">{status.message}</p>
          {status.consensus_progress?.recommendation && (
            <p className="text-sm text-zinc-500 bg-zinc-800/50 p-3 rounded-lg">
              💡 {status.consensus_progress.recommendation}
            </p>
          )}
        </div>
      </div>
    );
  }
  
  const layers = status?.consensus_progress?.layers || {
    gate: { status: 'pending' },
    fast: { status: 'pending' },
    mid: { status: 'pending' },
    deep: { status: 'pending' },
  };
  
  const completedCount = Object.values(layers).filter(l => l.status === 'complete').length;
  const progress = (completedCount / 4) * 100;
  
  return (
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" data-testid="consensus-overlay">
      <div className="bg-zinc-900/95 border border-zinc-700 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Brain className="w-6 h-6 text-yellow-400" />
            <h3 className="text-xl font-bold text-white">Multi-Model Consensus</h3>
          </div>
          <p className="text-zinc-400 text-sm">{status?.message || 'Initializing analysis...'}</p>
        </div>
        
        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
            <span>Progress</span>
            <span>{completedCount}/4 layers</span>
          </div>
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-yellow-500 to-green-500 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        
        {/* Layer status grid */}
        <div className="space-y-2 mb-6">
          {['gate', 'fast', 'mid', 'deep'].map(layer => (
            <LayerStatus key={layer} layer={layer} result={layers[layer] || { status: 'pending' }} />
          ))}
        </div>
        
        {/* Current consensus preview */}
        {status?.current_consensus && (
          <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase tracking-wide">Current Consensus</span>
              {status.current_consensus.is_provisional && (
                <span className="text-[10px] bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                  PROVISIONAL
                </span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <div>
                <div className="text-2xl font-bold text-white">
                  {((status.current_consensus.confidence || 0) * 100).toFixed(0)}%
                </div>
                <div className="text-xs text-zinc-500">Confidence</div>
              </div>
              <div className="h-10 w-px bg-zinc-700" />
              <div>
                <div className="text-lg font-semibold text-yellow-400 uppercase">
                  {status.current_consensus.pattern_type || 'Analyzing...'}
                </div>
                <div className="text-xs text-zinc-500">Pattern Type</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
